import { useRef, useState } from "react";
import { Modal, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { formatPrice } from "../../helpers/formattedPrice";
import closeIcon from "../../assets/icons/buy-and-supply-close-button.svg";
import greenMatterLogo from "../../assets/images/logo2.png";

export default function WithdrawalInvoiceModal({
  open,
  onCancel,
  withdrawal,
  currency,
}) {
  const { t } = useTranslation();
  const { dir } = useSelector((state) => state.language);
  const invoiceRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    const element = invoiceRef.current;
    if (!element || !withdrawal) return;

    setIsDownloading(true);
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;

      const canvasRatio = canvas.width / canvas.height;
      const pageRatio = maxWidth / maxHeight;

      let renderWidth;
      let renderHeight;

      if (canvasRatio > pageRatio) {
        renderWidth = maxWidth;
        renderHeight = maxWidth / canvasRatio;
      } else {
        renderHeight = maxHeight;
        renderWidth = maxHeight * canvasRatio;
      }

      const x = margin + (maxWidth - renderWidth) / 2;
      const y = margin + (maxHeight - renderHeight) / 2;

      pdf.addImage(imgData, "JPEG", x, y, renderWidth, renderHeight);
      pdf.save(`invoice-${withdrawal.id || "withdrawal"}.pdf`);
    } catch {
      window.print();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      centered
      destroyOnClose
      open={open}
      footer={false}
      closable
      maskClosable={false}
      closeIcon={<img src={closeIcon} alt="" className="d-block img-fluid" />}
      width={window.innerWidth >= 768 ? 480 : "100%"}
      className="buy-supply-2026-withdrawal-invoice-modal"
      onCancel={onCancel}
    >
      <div dir={dir} className="buy-supply-2026-withdrawal-invoice-modal__body">
        <div
          ref={invoiceRef}
          className="buy-supply-2026-withdrawal-invoice-modal__capture"
        >
          <div className="buy-supply-2026-withdrawal-invoice-modal__header">
            <img
              src={greenMatterLogo}
              alt="GreenMatter"
              className="buy-supply-2026-withdrawal-invoice-modal__logo"
              loading="lazy"
            />
            <h2 className="buy-supply-2026-withdrawal-invoice-modal__title">
              {t("buyAndSupply2026.invoice")}
            </h2>
          </div>

          <ul className="buy-supply-2026-withdrawal-invoice-modal__rows">
            <li className="buy-supply-2026-withdrawal-invoice-modal__row">
              <span className="buy-supply-2026-withdrawal-invoice-modal__label">
                {t("buyAndSupply2026.clientName")}
              </span>
              <span className="buy-supply-2026-withdrawal-invoice-modal__value">
                {withdrawal?.clientName || t("buyAndSupply2026.emDash")}
              </span>
            </li>
            <li className="buy-supply-2026-withdrawal-invoice-modal__row">
              <span className="buy-supply-2026-withdrawal-invoice-modal__label">
                {t("buyAndSupply2026.date")}
              </span>
              <span className="buy-supply-2026-withdrawal-invoice-modal__value">
                {withdrawal?.creationDate || t("buyAndSupply2026.emDash")}
              </span>
            </li>
            <li className="buy-supply-2026-withdrawal-invoice-modal__row">
              <span className="buy-supply-2026-withdrawal-invoice-modal__label">
                {t("buyAndSupply2026.withdrawalAmountIn", { currency })}
              </span>
              <span className="buy-supply-2026-withdrawal-invoice-modal__value">
                {formatPrice(withdrawal?.amount)}
              </span>
            </li>
          </ul>
        </div>

        <div className="buy-supply-2026-withdrawal-invoice-modal__actions">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            className="buy-supply-2026-withdrawal-invoice-modal__download-btn"
            onClick={handleDownloadPdf}
            loading={isDownloading}
          >
            {t("buyAndSupply2026.downloadInvoice")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
